import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { scrollToSection } from "./Navbar";

const HERO_IMG =
  "https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?q=80&w=2000&auto=format&fit=crop";

const LINES = ["WE CRAFT", "GOLDEN", "MOMENTS"];

function Particles() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let raf;
    let dots = [];

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      dots = Array.from({ length: 60 }, () => ({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        r: Math.random() * 1.8 + 0.4,
        vy: -(Math.random() * 0.35 + 0.08),
        vx: (Math.random() - 0.5) * 0.15,
        a: Math.random() * 0.5 + 0.15,
      }));
    };
    resize();
    window.addEventListener("resize", resize);

    const tick = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const d of dots) {
        d.y += d.vy;
        d.x += d.vx;
        if (d.y < -5) {
          d.y = canvas.height + 5;
          d.x = Math.random() * canvas.width;
        }
        ctx.beginPath();
        ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(212, 175, 55, ${d.a})`;
        ctx.fill();
      }
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden="true"
    />
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const fade = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img
          src={HERO_IMG}
          alt="Live concert with golden lights"
          className="w-full h-full object-cover scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#09090b]/80 via-[#09090b]/60 to-[#09090b]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(9,9,11,0.7)_100%)]" />
      </motion.div>

      <Particles />

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 w-full pt-28 pb-20"
      >
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          data-testid="hero-eyebrow"
          className="text-xs sm:text-sm uppercase tracking-[0.4em] text-[#d4af37] mb-8 flex items-center gap-4"
        >
          <span className="inline-block w-12 h-px bg-[#d4af37]" />
          SK Media Events — Bengaluru
        </motion.p>

        <h1
          data-testid="hero-title"
          className="font-serif-display font-bold uppercase leading-[0.95] tracking-tight text-5xl sm:text-7xl lg:text-8xl"
        >
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${i === 1 ? "gold-text" : "text-white"}`}
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{
                  delay: 0.35 + i * 0.18,
                  duration: 1,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.9 }}
          data-testid="hero-subtitle"
          className="mt-8 max-w-xl text-base sm:text-lg text-zinc-400 leading-relaxed"
        >
          Drum circles that move crowds, venues sculpted in light, artists and
          bands curated to perfection — we produce events people never forget.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.35, duration: 0.9 }}
          className="mt-12 flex flex-wrap items-center gap-6"
        >
          <button
            data-testid="hero-cta-enquire"
            onClick={() => scrollToSection("#enquiry")}
            className="group relative px-10 py-4 bg-[#d4af37] text-[#09090b] text-xs uppercase tracking-[0.25em] font-semibold overflow-hidden"
          >
            <span className="relative z-10">Start Your Enquiry</span>
            <span className="absolute inset-0 bg-[#f3e5ab] translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out" />
          </button>
          <button
            data-testid="hero-cta-gallery"
            onClick={() => scrollToSection("#gallery")}
            className="text-xs uppercase tracking-[0.25em] text-zinc-300 hover:text-[#d4af37] border-b border-[rgba(212,175,55,0.4)] pb-1 transition-colors duration-300"
          >
            View Our Work
          </button>
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToSection("#services")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#d4af37] animate-float-slow"
        aria-label="Scroll down"
      >
        <ChevronDown size={28} />
      </motion.button>
    </section>
  );
}
